import Vue, {PropType} from "vue";
import {
    SeoMetricValue,
    seoScoreContext,
    SeoScoreContext,
    SeoScoreMetricName,
    seoScoreMetricNames,
    SeoScoreStatus
} from "@/model/SeoScore";
import {ArticleMetasConfig} from "@/config/ArticleConfig";
import {countStemKeywordHitsForPhrases} from "@/service/keywordStemDensity";
import {ArticlePart, ArticlePartContent, Keyword, KeywordForAi} from "@/model/Article";
import {KeywordType} from "@/enum/keyword_type";
import {stripTagsAndEntities} from "@/utils/string";
import {getContentToExport} from "@/service/articleExport";
import {DownloadType} from "@/enum/download_type";
import {seoScoreMetrics} from "@/plugins/SeoScore";
import {focusSeoMetric} from "@/service/seoScoreFocus";
import {Image} from "@/model/Scrapper";
import {FALLBACK_URL, getBaseUrl, getSuggestedUrl, urlPathContainsKeyword} from "@/utils/ArticleUrl";
import {resolveArticleMainKeyword} from '@/service/articleMainKeyword';
import {hasValidSchemaMarkup} from "@/service/seoScoreSchema";
import {evaluateAiVisibility, evaluateEeat, qaPairsFromArticle} from "@/service/geoEatScore";
import {commitArticleSeoScore, readSeoScore} from "@/service/seoScoreCommit";

export default Vue.extend({
    name: "SeoScoreMetricsMixin",
    props: {
        context: {
            type: Object as PropType<SeoScoreContext>,
            default: () => seoScoreContext
        },
        demo: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        forceZeroScore(): boolean {
            return this.$store.getters.articleWordCount.getSum() === 0
                || this.$store.getters.articleAiLoading.loadingRatioTotal > 0.9;
        },
        stats(): Record<SeoScoreStatus, number> {
            return readSeoScore(() => {
                if (this.demo) {
                    return {
                        GREEN: seoScoreMetricNames.length,
                        YELLOW: 0,
                        RED: 0,
                    };
                }
                if (this.forceZeroScore) {
                    return {
                        GREEN: 0,
                        YELLOW: 0,
                        RED: seoScoreMetricNames.length,
                    };
                }
                const results = seoScoreMetricNames.map((metricName: SeoScoreMetricName) => this.getStatus(metricName));
                return {
                    GREEN: results.filter(status => status === 'GREEN').length,
                    YELLOW: results.filter(status => status === 'YELLOW').length,
                    RED: results.filter(status => status === 'RED').length,
                };
            });
        },
        seoScore(): number {
            return readSeoScore(() => {
                const stats = this.stats;
                return Math.round(((stats.GREEN + (stats.YELLOW / 2)) / seoScoreMetricNames.length) * 100);
            });
        },
        checkedKeywords(): Array<string> {
            return this.$store.getters.article.allKeywords
                .filter((item: Keyword) => item.keyword.trim().length)
                .filter((item: Keyword) => item.checked)
                .map((item: Keyword) => item.keyword);
        },
        mergedArticleText(): string {
            // Prefer the idle snapshot from ArticleCreation so CKEditor
            // keystrokes do not re-tokenize the article for the SEO pie.
            if (this.context && this.context.mergedText) {
                return this.context.mergedText;
            }
            let merged = '';
            for (const {value: part} of this.$store.getters.article.getAllParts()) {
                merged += part.reduce((acc: string, subpart: ArticlePartContent) => `${acc} | ${subpart.h} | ${subpart.p} `, '');
            }
            merged += this.$store.getters.article.questions.reduce(
                (acc: string, question: { question: string; answer: string }) => `${acc} | ${question.question} | ${question.answer} `,
                ''
            );
            return stripTagsAndEntities(merged);
        },
        computedArticleDensity(): number {
            if (typeof this.context.articleDensity === 'number' && this.context.mergedText) {
                return this.context.articleDensity;
            }
            const words = this.checkedKeywords.filter((word: string) => word && word.trim());
            const wordCount = this.$store.getters.articleWordCount.getSum() || 1;
            if (!words.length) {
                return this.context.articleDensity || 0;
            }
            const language = this.$store.getters.article.language_code || 'cs';
            const counts = countStemKeywordHitsForPhrases(this.mergedArticleText, words, language);
            const occurrences = words.reduce((acc: number, word: string) => acc + (counts[word] || 0), 0);
            return (occurrences / wordCount) * 100;
        },
        schemaMarkup(): Array<SeoMetricValue> {
            return [hasValidSchemaMarkup(this.$store.getters.article.schemaJsonLd)];
        },
        aiVisibility(): Array<SeoMetricValue> {
            return [evaluateAiVisibility(this.$store.getters.article).score];
        },
        eeatScore(): Array<SeoMetricValue> {
            return [evaluateEeat(this.$store.getters.article).score];
        },
        eatAuthor(): Array<SeoMetricValue> {
            return [!!(this.$store.getters.article.eatAttribution && this.$store.getters.article.eatAttribution.authorName)];
        },
        eatDate(): Array<SeoMetricValue> {
            const eat = this.$store.getters.article.eatAttribution || {};
            return [!!(eat.datePublished || eat.dateModified)];
        },
        eatBio(): Array<SeoMetricValue> {
            const bio = (this.$store.getters.article.eatAttribution && this.$store.getters.article.eatAttribution.authorBio) || '';
            return [bio.trim().length >= 40];
        },
        faqPresent(): Array<SeoMetricValue> {
            return [qaPairsFromArticle(this.$store.getters.article).length];
        },
        keywordVariants(): Array<SeoMetricValue> {
            return [this.$store.getters.article.allKeywords
                .filter((item: Keyword) => item.keyword.trim().length)
                .length];
        },
        keywordInSlug(): Array<SeoMetricValue> {
            const url = this.$store.getters.article.url;
            const selectedKeyword = resolveArticleMainKeyword(this.$store.getters.article);
            const suggested = getSuggestedUrl(url ? getBaseUrl(url) : FALLBACK_URL, selectedKeyword);
            if (!url) {
                return [false, selectedKeyword, suggested];
            }
            return [urlPathContainsKeyword(url, selectedKeyword), selectedKeyword, suggested];
        },
        imgAltIncludesKeyword(): Array<SeoMetricValue> {
            const selectedKeyword = this.$store.getters.article.selectedKeyword;
            const alts = this.$store.getters.article.images.map((image: Image) => image.alt).map((alt: string) => alt.toLowerCase());
            return [alts.some((alt: string) => alt.includes(selectedKeyword.toLowerCase())), selectedKeyword];
        },
        titleIncludesKeyword(): Array<SeoMetricValue> {
            return [this.$store.getters.article.metaTitle, this.checkedKeywords];
        },
        titleLength(): Array<SeoMetricValue> {
            return [stripTagsAndEntities(this.$store.getters.article.metaTitle).length,
                ArticleMetasConfig.metaTitle.suggestedLength];
        },
        descriptionIncludesKeyword(): Array<SeoMetricValue> {
            return [this.$store.getters.article.metaDescription, this.checkedKeywords];
        },
        descriptionLength(): Array<SeoMetricValue> {
            return [stripTagsAndEntities(this.$store.getters.article.metaDescription).length,
                ArticleMetasConfig.metaDescription.suggestedLength];
        },
        keywordDensity(): Array<SeoMetricValue> {
            return [this.computedArticleDensity];
        },
        serpIsComplete(): Array<SeoMetricValue> {
            return [(this.$store.getters.article.metaTitle.length > 0
                && this.$store.getters.article.metaDescription.length > 0)];
        },
        avgWordCountOfTopTen(): Array<SeoMetricValue> {
            return [this.$store.getters.articleWordCount.getSum(),
                this.context.topTenGeometricAverageWordCount];
        },
        longestWordCountOfTopTen(): Array<SeoMetricValue> {
            return [this.$store.getters.articleWordCount.getSum(),
                this.context.topTenLongestWordCount];
        },
        keywordInIntroduction(): Array<SeoMetricValue> {
            return [
                this.$store.getters.article.h1[0]?.p,
                this.checkedKeywords,
                this.$store.getters.article.language_code || 'cs',
            ];
        },
        keywordInH1(): Array<SeoMetricValue> {
            return [this.$store.getters.article.h1.map((h1: ArticlePartContent) => h1.h), this.checkedKeywords];
        },
        keywordInH2(): Array<SeoMetricValue> {
            return [this.$store.getters.article.h2.map((h2: ArticlePartContent) => h2.h), this.checkedKeywords];
        },
        keywordInH3(): Array<SeoMetricValue> {
            return [this.$store.getters.article.h3.map((h3: ArticlePartContent) => h3.h), this.checkedKeywords];
        },
        relatedKeywords(): Array<SeoMetricValue> {
            const selectedRelatedKeywords = (this.$store.getters.article.keywordsForAi || [])
                .filter(((item: KeywordForAi) => item.type === KeywordType.RELATED))
                .map((item: KeywordForAi) => item.keyword);
            const articleContent = this.mergedArticleText;
            const language = this.$store.getters.article.language_code || 'cs';
            const counts = countStemKeywordHitsForPhrases(articleContent, selectedRelatedKeywords, language);
            const usedKeywords = selectedRelatedKeywords
                .filter((item: string) => (counts[item] || 0) > 0);
            return [usedKeywords.length / (selectedRelatedKeywords.length || 1)];
        },
        inboundLinks(): Array<SeoMetricValue> {
            const content = getContentToExport(this.$store.getters.article, DownloadType.HTML)[0];
            const relativeLinksLength = (content.match(new RegExp(/(<a.*?href="\/.*?<\/a>)/, 'gi'))
                || [])
                .filter(match => !(match.includes('href="#') || match.includes("href='#'")))
                .length;
            const sameSiteLinksLength = (content.match(new RegExp(/(<a.*?href="\w.*?<\/a>)/, 'gi'))
                || [])
                .filter(match => !(match.includes('href="#') || match.includes("href='#'")))
                .filter(match => this.homeDomain ? match.includes(this.homeDomain) : true)
                .length;
            return [relativeLinksLength + sameSiteLinksLength];
        },
        outboundLinks(): Array<SeoMetricValue> {
            return [(getContentToExport(this.$store.getters.article, DownloadType.HTML)[0]
                .match(new RegExp(/(<a.*?href="\w.*?<\/a>)/, 'gi'))
                || [])
                .filter(match => !(match.includes('href="#') || match.includes("href='#'")))
                .filter(match => this.homeDomain ? !match.includes(this.homeDomain) : true)
                .length];
        },
        inspirationFromTopTen(): Array<SeoMetricValue> {
            return [true];
        },
        subHeadingDistribution(): Array<SeoMetricValue> {
            const total = this.$store.getters.article.getAllParts().length;
            const missing = this.$store.getters.article.getAllParts()
                .map((part: ArticlePart) => part.value)
                .map((partContents: ArticlePartContent[]) => {
                    return partContents.filter((partContent: ArticlePartContent) =>
                        partContent.h.length === 0 && partContent.p.length !== 0
                    ).length;
                })
                .filter((missingHeadings: number) => missingHeadings > 0)
                .length;
            return [total - missing, total];
        },
        paragraphLength(): Array<SeoMetricValue> {
            const paragraphWordCounts = (
                getContentToExport(this.$store.getters.article, DownloadType.HTML)[0]
                    .match(new RegExp(/(<p[^>]*>[^>]*<\/p>)/, 'gi'))
                || []).map((paragraph: string) => paragraph.split(' ').length);
            const shortParagraphs = paragraphWordCounts.filter((words: number) => words < 150);
            return [shortParagraphs.length / (paragraphWordCounts.length || 1)];
        },
        sentenceLength(): Array<SeoMetricValue> {
            let sentenceCount = 0;
            let totalWordCount = 0;
            this.$store.getters.article.getAllParts()
                .map((part: ArticlePart) => part.value)
                .forEach((partContents: ArticlePartContent[]) => {
                    partContents.map((part: ArticlePartContent) => {
                        totalWordCount += part.p.split(' ').length;
                        sentenceCount += (part.p.match(new RegExp(/[.!?。！？;¿¡]/, 'gi')) || []).length;
                    });
                });
            return [Math.round((totalWordCount / (sentenceCount || 1)) * 10) / 10];
        },
        homeDomain(): string | null {
            try {
                const url = this.$store.getters.article.url;
                const host = new URL(url !== '' ? url : FALLBACK_URL).host;
                return host;
            } catch {
                return null;
            }
        },
        articleWordTotal(): number {
            return this.$store.getters.articleWordCount.getSum();
        },
        articleReadMinutes(): number {
            return Math.max(1, Math.round(this.articleWordTotal / 200) || 1);
        }
    },
    watch: {
        seoScore: function (newScore: number, oldScore: number) {
            if (this.demo) {
                return;
            }
            // Compare with the stored number and ignore re-entry from this read.
            commitArticleSeoScore(this.$store, newScore, oldScore);
        },
    },
    methods: {
        getStatus(metricName: SeoScoreMetricName): SeoScoreStatus {
            return readSeoScore(() => {
                const result: SeoMetricValue | Array<SeoMetricValue> = this[metricName];
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                return seoScoreMetrics[metricName].getStatus(...result);
            });
        },
        getTrParams(metricName: SeoScoreMetricName): Array<SeoMetricValue | undefined> {
            const values: SeoMetricValue | Array<SeoMetricValue> = this[metricName];
            if (metricName === 'keywordInSlug' && Array.isArray(values)) {
                return [values[1], values[2]];
            }
            if (metricName === 'keywordInIntroduction' && Array.isArray(values)) {
                return [values[0], values[1]];
            }
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            return seoScoreMetrics[metricName].getTrParams?.(...values) || values;
        },
        getClickAction(metricName: SeoScoreMetricName): (() => void) | undefined {
            if (!seoScoreMetrics[metricName]) {
                return undefined;
            }
            return () => focusSeoMetric(metricName);
        }
    }
});
